import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  AppBar,
  Badge,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Toolbar,
  Typography,
  Tooltip,
} from "@mui/material";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import MenuIcon from "@mui/icons-material/Menu";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ListAltIcon from "@mui/icons-material/ListAlt";
import TimelineIcon from "@mui/icons-material/Timeline";
import LogoutIcon from "@mui/icons-material/Logout";
import ApartmentIcon from "@mui/icons-material/Apartment";
import PeopleIcon from "@mui/icons-material/People";
import HistoryIcon from "@mui/icons-material/History";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import { useAuth } from "../../auth/contexts/AuthContext";
import { useNavigate, useLocation, useNavigationType } from "react-router-dom";
import { useThemeContext } from "../contexts/ThemeContext";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import Switch from "@mui/material/Switch";
import { useTheme, useMediaQuery } from "@mui/material";
import api from "../../../config/axios";
import websocketService from "../../../services/websocket";
import { getOverlayContainer } from "../../../theme/containerHelper";
import {
  playMailSound,
  unlockMailAudio,
} from "../../../services/mailNotificationSound";
import {
  mailNotificationPermission,
  registerMailNotificationWorker,
  requestMailNotificationPermission,
  showMailNotification,
  showMailNotificationEnabled,
  type MailNotificationPermission,
} from "../../../services/mailNotifications";
import { useCloseOnBack } from "../hooks/useCloseOnBack";
import { useRouteScrollRestoration } from "../hooks/useRouteScrollRestoration";
import { useAvisosArca } from "../../clients/hooks/useAvisosArca";

const drawerWidth = 100;
const railDrawerWidth = 72;
const MAIL_BADGE_REFRESH_MS = 30000;

// El polling y el WebSocket pueden avisar del mismo correo con milisegundos de
// diferencia. Sin esta ventana, según cuál llegue primero sonaba una o dos veces.
const MAIL_NOTIFY_DEDUPE_MS = 3000;

type LayoutProps = {
  children: React.ReactNode;
};

const Layout = ({ children }: LayoutProps) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const { mode, toggleMode } = useThemeContext();
  const theme = useTheme();
  const isShortDesktop = useMediaQuery("(min-width:900px) and (max-height:820px)");
  const [mailEnabled, setMailEnabled] = useState(false);
  const [unreadMailCount, setUnreadMailCount] = useState(0);
  const unreadMailCountRef = useRef(0);
  const unreadCountInitializedRef = useRef(false);
  const lastNotifiedAtRef = useRef(0);
  const mainRef = useRef<HTMLElement | null>(null);
  const [notificationPermission, setNotificationPermission] =
    useState<MailNotificationPermission>(() => mailNotificationPermission());

  const effectiveDrawerWidth = isShortDesktop ? railDrawerWidth : drawerWidth;
  const isCleaningStaff = user?.rol === "limpieza";
  useAvisosArca(user?.email);
  // Espeja la restricción del backend: sólo super_admin y admin leen los logs.
  const canReadLogs = user?.rol === "super_admin" || user?.rol === "admin";

  /**
   * Punto único donde se decide avisar. Antes el polling y el WebSocket
   * decidían por separado y con condiciones distintas, así que un mismo correo
   * podía sonar dos veces o ninguna según cuál llegara primero.
   */
  const handleUnreadUpdate = useCallback(
    (nextUnreadCount: number, createdCount: number | null) => {
      const yaInicializado = unreadCountInitializedRef.current;
      const hayCorreoNuevo = createdCount !== null
        ? createdCount > 0
        : nextUnreadCount > unreadMailCountRef.current;
      const ahora = Date.now();

      unreadMailCountRef.current = nextUnreadCount;
      unreadCountInitializedRef.current = true;
      setUnreadMailCount(nextUnreadCount);

      if (!yaInicializado || !hayCorreoNuevo) return;
      if (ahora - lastNotifiedAtRef.current < MAIL_NOTIFY_DEDUPE_MS) return;
      lastNotifiedAtRef.current = ahora;

      // El sonido siempre suena con nuestro propio synth: no se puede asumir que
      // la notificación del sistema trae sonido audible (depende del SO/navegador
      // y de la configuración de foco/no molestar del usuario).
      void playMailSound();

      // La notificación del sistema es un aviso adicional para cuando no se está
      // mirando la pestaña; si está mirando, el sonido ya alcanza.
      const usuarioMirando =
        document.visibilityState === "visible" && document.hasFocus();
      if (!usuarioMirando) {
        void showMailNotification(nextUnreadCount);
      }
    },
    []
  );

  const handleNotificationButtonClick = async () => {
    if (notificationPermission === "granted") {
      // Ya está activado: el botón sirve para probar sonido + notificación juntos.
      void playMailSound();
      void showMailNotificationEnabled();
      return;
    }

    const result = await requestMailNotificationPermission();
    setNotificationPermission(result);
    if (result === "granted") {
      void playMailSound();
      void showMailNotificationEnabled();
    }
  };

  useEffect(() => {
    websocketService.connect();
  }, []);

  useEffect(() => {
    const unlockAudio = () => {
      void unlockMailAudio();
    };

    window.addEventListener("pointerdown", unlockAudio);
    window.addEventListener("keydown", unlockAudio);
    return () => {
      window.removeEventListener("pointerdown", unlockAudio);
      window.removeEventListener("keydown", unlockAudio);
    };
  }, []);

  // Si el permiso ya estaba dado de una sesión anterior, dejar el worker listo
  // antes de que llegue el primer correo.
  useEffect(() => {
    if (mailNotificationPermission() === "granted") {
      void registerMailNotificationWorker();
    }
  }, []);

  useEffect(() => {
    if (isCleaningStaff) {
      setMailEnabled(false);
      setMobileOpen(false);
      return;
    }
    api.get("/mail/status")
      .then((response) => setMailEnabled(Boolean(response.data?.enabled)))
      .catch(() => setMailEnabled(false));
  }, [isCleaningStaff]);

  useEffect(() => {
    if (!mailEnabled) return;

    const refreshUnreadCount = async () => {
      try {
        const response = await api.get("/mail/unread-count");
        // El polling no sabe cuántos llegaron: infiere por aumento del contador.
        handleUnreadUpdate(Number(response.data?.unread || 0), null);
      } catch {
        unreadMailCountRef.current = 0;
        unreadCountInitializedRef.current = true;
        setUnreadMailCount(0);
      }
    };

    refreshUnreadCount();
    window.addEventListener("mail-unread-refresh", refreshUnreadCount);
    const unsubscribeMailSocket = websocketService.onMailUnreadChanged((data) => {
      handleUnreadUpdate(Number(data.unread || 0), Number(data.created || 0));
    });
    const countTimer = window.setInterval(refreshUnreadCount, MAIL_BADGE_REFRESH_MS);
    return () => {
      window.removeEventListener("mail-unread-refresh", refreshUnreadCount);
      unsubscribeMailSocket();
      window.clearInterval(countTimer);
    };
  }, [mailEnabled, handleUnreadUpdate]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  useCloseOnBack(mobileOpen, () => setMobileOpen(false));
  useRouteScrollRestoration(location, navigationType, mainRef);

  const menuItems = isCleaningStaff
    ? [{ text: "Limpieza", icon: <CleaningServicesIcon />, path: "/limpieza-diaria" }]
    : [
        { text: "Calendario", icon: <CalendarMonthIcon />, path: "/calendario" },
        { text: "Reservas", icon: <ListAltIcon />, path: "/reservas" },
        { text: "Estadísticas", icon: <TimelineIcon />, path: "/estadisticas" },
        { text: "Departamentos", icon: <ApartmentIcon />, path: "/departamentos" },
        { text: "Inventario", icon: <Inventory2Icon />, path: "/inventario" },
        { text: "Limpieza", icon: <CleaningServicesIcon />, path: "/limpieza" },
        ...(mailEnabled ? [{
          text: "Correo",
          icon: (
            <Badge
              badgeContent={unreadMailCount}
              color="error"
              max={99}
              invisible={unreadMailCount === 0}
              overlap="circular"
            >
              <MailOutlineIcon />
            </Badge>
          ),
          path: "/correo",
        }] : []),
        { text: "Clientes", icon: <PeopleIcon />, path: "/clientes" },
        ...(canReadLogs ? [{ text: "Historial", icon: <HistoryIcon />, path: "/logs" }] : []),
      ];

  const renderMenuItem = (item: typeof menuItems[0]) => {
    const isSelected = location.pathname === item.path;
    const buttonContent = (
      <ListItemButton
        selected={isSelected}
        onClick={() => {
          if (location.pathname !== item.path) {
            navigate(item.path, { replace: mobileOpen });
          }
          setMobileOpen(false);
        }}
        sx={
          isShortDesktop
            ? {
                minHeight: 42,
                justifyContent: "center",
                px: 0.5,
                flexDirection: "column",
                gap: 0,
                borderRadius: 1,
                mx: 0.5,
                my: 0.25,
              }
            : {
                minHeight: 48,
                justifyContent: "center",
                px: 2.5,
                flexDirection: "column",
                gap: 1,
              }
        }
      >
        <ListItemIcon
          sx={
            isShortDesktop
              ? { minWidth: 0, justifyContent: "center", mb: 0.25 }
              : { minWidth: 0, justifyContent: "center" }
          }
        >
          {item.icon}
        </ListItemIcon>
        {!isShortDesktop && (
          <ListItemText
            primary={item.text}
            sx={{
              textAlign: "center",
              "& .MuiTypography-root": {
                fontSize: "0.75rem",
              },
            }}
          />
        )}
      </ListItemButton>
    );

    if (isShortDesktop) {
      return (
        <Tooltip key={item.text} title={item.text} placement="right" arrow>
          <ListItem disablePadding sx={{ display: "block" }}>
            {buttonContent}
          </ListItem>
        </Tooltip>
      );
    }

    return (
      <ListItem key={item.text} disablePadding sx={{ display: "block" }}>
        {buttonContent}
      </ListItem>
    );
  };

  const drawer = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Toolbar sx={{ minHeight: isShortDesktop ? 56 : 64 }} />
      <List
        sx={{
          flexGrow: 1,
          px: isShortDesktop ? 0.25 : 0,
          overflowY: isShortDesktop ? "auto" : "visible",
          overflowX: "hidden",
          "&::-webkit-scrollbar": { width: 4 },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: "divider",
            borderRadius: 4,
          },
        }}
      >
        {menuItems.map((item) => renderMenuItem(item))}
      </List>

      {/* Botón de cerrar sesión al fondo */}
      <Box
        sx={{
          borderTop: "1px solid",
          borderColor: "divider",
          mt: "auto",
        }}
      >
        {isShortDesktop ? (
          <Tooltip title="Cerrar Sesión" placement="right" arrow>
            <ListItemButton
              onClick={logout}
              sx={{
                minHeight: 42,
                justifyContent: "center",
                px: 0.5,
                flexDirection: "column",
                gap: 0,
                borderRadius: 1,
                mx: 0.5,
                my: 0.5,
              }}
            >
              <ListItemIcon sx={{ minWidth: 0, justifyContent: "center" }}>
                <LogoutIcon />
              </ListItemIcon>
            </ListItemButton>
          </Tooltip>
        ) : (
          <ListItemButton
            onClick={logout}
            sx={{
              minHeight: 48,
              justifyContent: "center",
              px: 2.5,
              flexDirection: "column",
              gap: 1,
            }}
          >
            <ListItemIcon sx={{ minWidth: 0, justifyContent: "center" }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText
              primary="Cerrar Sesión"
              sx={{
                textAlign: "center",
                "& .MuiTypography-root": {
                  fontSize: "0.75rem",
                },
              }}
            />
          </ListItemButton>
        )}
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        bgcolor: "background.default",
        color: "text.primary",
      }}
    >
      <AppBar
        position="fixed"
        sx={{
          left: { xs: 0, sm: `${effectiveDrawerWidth}px` },
          right: 0,
          width: { xs: "100%", sm: `calc(100% - ${effectiveDrawerWidth}px)` },
          bgcolor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          boxShadow: 1,
        }}
      >
        <Toolbar sx={{ position: "relative", minHeight: isShortDesktop ? 56 : 64 }}>
          {isCleaningStaff ? (
            <IconButton
              color="inherit"
              aria-label="Cerrar sesión"
              edge="start"
              onClick={logout}
              sx={{
                position: "absolute",
                left: 16,
                top: "50%",
                transform: "translateY(-50%)",
                display: { sm: "none" },
              }}
            >
              <LogoutIcon />
            </IconButton>
          ) : (
            <Badge
              badgeContent={unreadMailCount}
              color="error"
              max={99}
              invisible={!mailEnabled || unreadMailCount === 0}
              overlap="circular"
              sx={{
                position: "absolute",
                left: 16,
                top: "50%",
                transform: "translateY(-50%)",
                display: { sm: "none" },
                "& .MuiBadge-badge": {
                  right: 2,
                  top: 4,
                },
              }}
            >
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
              >
                <MenuIcon />
              </IconButton>
            </Badge>
          )}

          {/* Contenido centrado */}
          <Box
            sx={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              display: "flex",
              alignItems: "center",
              gap: 2,
              maxWidth: { xs: "40%", sm: "auto" },
            }}
          >
            <img
              src={mode === "dark" ? "/brand/logo-white.png" : "/brand/logo.png"}
              alt="Logo"
              style={{ height: isShortDesktop ? "32px" : "40px" }}
            />
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{
                color: theme.palette.text.primary,
                display: { xs: "none", sm: "block" },
                fontSize: isShortDesktop ? "1.05rem" : undefined,
              }}
            >
              {isCleaningStaff ? "LIMPIEZA" : "RESERVAS"}
            </Typography>
          </Box>

          {/* Switch dark/light a la derecha */}
          <Box
            sx={{
              position: "absolute",
              right: { xs: 8, sm: 24 },
              top: "50%",
              transform: "translateY(-50%)",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            {mailEnabled && notificationPermission !== "unsupported" ? (
              <Tooltip
                title={
                  notificationPermission === "granted"
                    ? "Avisos activados · tocar para probar sonido y aviso"
                    : notificationPermission === "denied"
                      ? "Avisos bloqueados. Habilitalos desde la configuración del navegador para este sitio."
                      : "Activar avisos de correo nuevo"
                }
              >
                {/* span: un IconButton deshabilitado no dispara el Tooltip */}
                <span>
                  <IconButton
                    color="inherit"
                    size="small"
                    aria-label={
                      notificationPermission === "granted"
                        ? "Probar sonido y aviso de correo"
                        : "Activar avisos de correo nuevo"
                    }
                    onClick={handleNotificationButtonClick}
                    disabled={notificationPermission === "denied"}
                  >
                    {notificationPermission === "granted" ? (
                      <NotificationsActiveIcon fontSize="small" />
                    ) : notificationPermission === "denied" ? (
                      <NotificationsOffIcon fontSize="small" />
                    ) : (
                      <NotificationsNoneIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
            {mode === "dark" ? <Brightness4Icon /> : <Brightness7Icon />}
            <Switch
              checked={mode === "dark"}
              onChange={toggleMode}
              color="default"
              size="small"
            />
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{
          width: { sm: effectiveDrawerWidth },
          flexShrink: { sm: 0 },
          display: { xs: isCleaningStaff ? "none" : "block", sm: "block" },
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            container: getOverlayContainer,
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: effectiveDrawerWidth,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        ref={mainRef}
        sx={{
          flexGrow: 1,
          minWidth: 0,
          maxWidth: "100%",
          height: "100dvh",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          bgcolor: "background.default",
          color: "text.primary",
        }}
      >
        <Toolbar sx={{ minHeight: isShortDesktop ? 56 : 64 }} />
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
